/* Banking Portal · Wave-8 — Security & Fraud Posture (Feb 2026).
   Sponsor-bank-facing executive scorecard for cyber + fraud defense.
   Single page at /banking/security-posture · 4 tabs:
     1. Threat Defense
     2. Fraud Detection
     3. Access Governance
     4. Cyber Posture
   100% live signals · zero mocks. Read-only.
*/
import { useEffect, useMemo, useState } from "react";
import api from "../../lib/api";
import {
  Shield, ShieldAlert, ShieldCheck, AlertTriangle, Activity,
  Lock, Eye, Users, KeyRound, FileCheck, CheckCircle2, XCircle,
  Globe, Ban, Gauge, Clock, FileSearch, Award, ServerCrash,
} from "lucide-react";
import { Badge } from "../../components/ui/badge";
import {
  Tabs, TabsList, TabsTrigger, TabsContent,
} from "../../components/ui/tabs";

const KS_TONE = {
  normal:         "bg-emerald-50 text-emerald-700 border-emerald-200",
  read_only:      "bg-amber-50 text-amber-700 border-amber-200",
  signups_locked: "bg-amber-50 text-amber-700 border-amber-200",
  admin_only:     "bg-orange-50 text-orange-700 border-orange-200",
  full_freeze:    "bg-rose-50 text-rose-700 border-rose-200",
};

function fmt(n) {
  if (n == null) return "—";
  return new Intl.NumberFormat("en-US").format(n);
}

function Kpi({ label, value, sub, tone = "slate", icon: Icon, testid }) {
  const toneMap = {
    slate:   "bg-slate-50 border-slate-200",
    emerald: "bg-emerald-50 border-emerald-200",
    amber:   "bg-amber-50 border-amber-200",
    rose:    "bg-rose-50 border-rose-200",
    sky:     "bg-sky-50 border-sky-200",
    violet:  "bg-violet-50 border-violet-200",
  };
  return (
    <div data-testid={testid} className={`rounded-xl border ${toneMap[tone] || toneMap.slate} px-4 py-3`}>
      <div className="flex items-center justify-between">
        <div className="text-[10.5px] uppercase tracking-wider text-slate-500">{label}</div>
        {Icon && <Icon className="w-3.5 h-3.5 text-slate-400" />}
      </div>
      <div className="text-2xl font-semibold tabular-nums text-slate-900 mt-1.5">{value}</div>
      {sub && <div className="text-[10.5px] text-slate-500 mt-0.5">{sub}</div>}
    </div>
  );
}

function Card({ title, icon: Icon, children, testid, action }) {
  return (
    <div data-testid={testid} className="rounded-2xl border bg-white overflow-hidden">
      <div className="flex items-center justify-between px-4 py-3 border-b bg-slate-50/60">
        <div className="flex items-center gap-2">
          {Icon && <Icon className="w-4 h-4 text-slate-500" />}
          <div className="text-sm font-semibold text-slate-900">{title}</div>
        </div>
        {action}
      </div>
      <div className="p-4">{children}</div>
    </div>
  );
}

function LiveRibbon() {
  return (
    <div className="inline-flex items-center gap-1.5 rounded-full bg-emerald-50 border border-emerald-200 px-2.5 py-1 text-[10.5px] font-medium text-emerald-700" data-testid="wave8-live-ribbon">
      <span className="relative flex w-1.5 h-1.5">
        <span className="absolute inline-flex w-full h-full rounded-full bg-emerald-400 opacity-75 animate-ping" />
        <span className="relative inline-flex w-1.5 h-1.5 rounded-full bg-emerald-500" />
      </span>
      LIVE · 100% production signals
    </div>
  );
}

// ═════════════════════════════════════════════════════════════════════════
// TAB 1 · THREAT DEFENSE
// ═════════════════════════════════════════════════════════════════════════
function ThreatDefenseTab() {
  const [overview, setOverview] = useState(null);
  const [topIps, setTopIps] = useState(null);
  const [defenses, setDefenses] = useState(null);

  useEffect(() => {
    api.get("/partner/banking/security/threat-overview").then(r => setOverview(r.data)).catch(() => {});
    api.get("/partner/banking/security/top-attacking-ips?window_minutes=60&limit=10").then(r => setTopIps(r.data)).catch(() => {});
    api.get("/partner/banking/security/active-defenses").then(r => setDefenses(r.data)).catch(() => {});
  }, []);

  if (!overview || !topIps || !defenses) return <div className="text-sm text-slate-500" data-testid="threat-loading">Loading threat posture…</div>;

  return (
    <div className="space-y-5" data-testid="threat-defense-tab">
      {/* 4 KPI tiles */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Kpi testid="kpi-failed-1h"    label="Failed logins · 1h"  value={fmt(overview.failed_logins_1h)}   icon={ShieldAlert} tone={overview.failed_logins_1h > 50 ? "rose" : "emerald"} />
        <Kpi testid="kpi-failed-24h"   label="Failed logins · 24h" value={fmt(overview.failed_logins_24h)}  icon={ShieldAlert} tone={overview.failed_logins_24h > 200 ? "rose" : "emerald"} />
        <Kpi testid="kpi-ratelimit-24h" label="Rate-limit hits · 24h" value={fmt(overview.rate_limits_24h)} icon={Gauge} tone={overview.rate_limits_24h > 100 ? "amber" : "emerald"} />
        <Kpi testid="kpi-badsig-24h"   label="Webhook bad-sig · 24h" value={fmt(overview.webhook_bad_sig_24h)} icon={ServerCrash} tone={overview.webhook_bad_sig_24h > 0 ? "amber" : "emerald"} />
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Kpi testid="kpi-lockouts"     label="Lockouts · 24h"    value={fmt(overview.auth_lockouts_24h)} icon={Lock} tone="slate" />
        <Kpi testid="kpi-travel"       label="Impossible travel · 24h" value={fmt(overview.impossible_travel_24h)} icon={Globe} tone="slate" />
        <Kpi testid="kpi-blocked"      label="Active IP blocks"  value={fmt(overview.ip_blocks_active)}  icon={Ban} tone="violet" />
        <Kpi testid="kpi-blocked-hits" label="IP-block hits · 24h" value={fmt(overview.ip_blocks_hits_24h)} icon={Ban} tone="slate" />
      </div>

      {/* Kill switch */}
      <Card title="Kill switch — 5-level platform freeze" icon={ShieldAlert} testid="card-killswitch">
        <div className="flex flex-wrap items-center gap-3">
          <Badge data-testid="ks-current-level" variant="outline" className={`text-xs px-3 py-1 ${KS_TONE[defenses.kill_switch?.level] || KS_TONE.normal}`}>
            CURRENT · {defenses.kill_switch?.level?.replace("_", " ") || "normal"}
          </Badge>
          {defenses.kill_switch?.reason && (
            <span className="text-xs text-slate-600">reason: <span className="italic">{defenses.kill_switch.reason}</span></span>
          )}
          {defenses.kill_switch?.set_at && (
            <span className="text-[11px] text-slate-400 ml-auto">set: {new Date(defenses.kill_switch.set_at).toLocaleString()}</span>
          )}
        </div>
        <div className="mt-3 flex flex-wrap gap-2" data-testid="ks-levels">
          {(defenses.kill_switch?.available_levels || []).map(l => (
            <Badge key={l} variant="outline" className={`text-[10px] ${l === defenses.kill_switch?.level ? KS_TONE[l] : "bg-slate-50 text-slate-400 border-slate-200"}`}>
              {l.replace("_", " ")}
            </Badge>
          ))}
        </div>
        <div className="mt-3 text-[11px] text-slate-500">
          Engineering-scope only. Audit-chained on every transition. Bank stakeholders can request immediate elevation via the sponsor-bank escalation path.
        </div>
      </Card>

      {/* Top attacking IPs */}
      <Card title="Top attacking IPs · last 60 min" icon={Globe} testid="card-top-ips" action={<span className="text-[10.5px] text-slate-400">window: {topIps.window_minutes} min</span>}>
        {(topIps.top_ips || []).length === 0 ? (
          <div className="text-sm text-slate-500 italic" data-testid="topips-empty">No attacking IPs detected in the current window. (Baseline state · no defense action required.)</div>
        ) : (
          <div className="overflow-hidden rounded-lg border">
            <div className="grid grid-cols-12 px-3 py-2 bg-slate-50 text-[10.5px] uppercase tracking-wider text-slate-500 border-b">
              <div className="col-span-5">IP / CIDR</div>
              <div className="col-span-3 text-right">Events</div>
              <div className="col-span-2">Top kind</div>
              <div className="col-span-2">Severity</div>
            </div>
            {(topIps.top_ips || []).map((row, i) => (
              <div key={i} data-testid={`topip-row-${i}`} className="grid grid-cols-12 px-3 py-2 border-b border-slate-100 text-sm">
                <div className="col-span-5 font-mono text-[12px]">{row.ip || row._id || "—"}</div>
                <div className="col-span-3 text-right tabular-nums">{fmt(row.count || row.n || 0)}</div>
                <div className="col-span-2 text-[11px] font-mono text-slate-600">{row.top_kind || row.kind || "—"}</div>
                <div className="col-span-2"><Badge variant="outline" className="text-[10px]">{row.severity || "info"}</Badge></div>
              </div>
            ))}
          </div>
        )}
      </Card>

      {/* Auto-ban rules + blocklist */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card title="Auto-ban rules" icon={Ban} testid="card-autoban">
          <div className="space-y-2">
            {Object.entries(defenses.auto_ban_rules || {}).map(([rule, cfg]) => (
              <div key={rule} data-testid={`autoban-${rule}`} className="flex items-center justify-between gap-3 rounded-lg border bg-slate-50/60 px-3 py-2">
                <div>
                  <div className="text-[12px] font-medium font-mono text-slate-800">{rule}</div>
                  <div className="text-[10.5px] text-slate-500">
                    {cfg.enabled ? <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200 text-[9.5px] mr-1">ENABLED</Badge>
                                 : <Badge variant="outline" className="bg-slate-100 text-slate-500 text-[9.5px] mr-1">OFF</Badge>}
                    threshold {cfg.threshold} · window {cfg.window_minutes}m · ban {cfg.ban_hours}h
                  </div>
                </div>
              </div>
            ))}
            {Object.keys(defenses.auto_ban_rules || {}).length === 0 && (
              <div className="text-sm text-slate-500 italic">No auto-ban rules configured.</div>
            )}
          </div>
        </Card>

        <Card title={`Active IP blocklist (${defenses.blocked_ips_count})`} icon={ShieldCheck} testid="card-blocklist">
          {(defenses.blocked_ips || []).length === 0 ? (
            <div className="text-sm text-slate-500 italic" data-testid="blocklist-empty">No IPs currently blocked. (No active threats.)</div>
          ) : (
            <div className="space-y-2 max-h-72 overflow-y-auto">
              {defenses.blocked_ips.map((b, i) => (
                <div key={b.id || i} data-testid={`block-${i}`} className="rounded-lg border bg-slate-50/40 px-3 py-2">
                  <div className="flex items-center justify-between">
                    <span className="font-mono text-[12px] text-slate-800">{b.ip_or_cidr}</span>
                    {b.auto ? <Badge variant="outline" className="bg-violet-50 text-violet-700 border-violet-200 text-[9.5px]">AUTO</Badge>
                            : <Badge variant="outline" className="bg-sky-50 text-sky-700 border-sky-200 text-[9.5px]">MANUAL</Badge>}
                  </div>
                  <div className="text-[10.5px] text-slate-500 mt-0.5">{b.reason || "—"} · until {b.expires_at ? new Date(b.expires_at).toLocaleString() : "permanent"}</div>
                </div>
              ))}
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}

// ═════════════════════════════════════════════════════════════════════════
// TAB 2 · FRAUD DETECTION
// ═════════════════════════════════════════════════════════════════════════
function FraudDetectionTab() {
  const [fraud, setFraud] = useState(null);
  const [stuck, setStuck] = useState(null);
  useEffect(() => {
    api.get("/partner/banking/security/fraud-anomalies").then(r => setFraud(r.data)).catch(() => {});
    api.get("/partner/banking/security/stuck-ops").then(r => setStuck(r.data)).catch(() => {});
  }, []);
  if (!fraud || !stuck) return <div className="text-sm text-slate-500" data-testid="fraud-loading">Loading fraud detection…</div>;

  const ovr = fraud.override_anomalies || {};
  const aml = fraud.aml_pattern_engine || {};
  const san = fraud.sanctions_screening || {};
  const lend = fraud.lender_auto_alerts || {};

  return (
    <div className="space-y-5" data-testid="fraud-detection-tab">
      {/* Override anomaly engine */}
      <Card title="Override anomaly engine · 7 rules" icon={ShieldAlert} testid="card-override-anomalies"
            action={<Badge variant="outline" className="text-[10px]">last scan: {ovr.last_scan_at ? new Date(ovr.last_scan_at).toLocaleTimeString() : "—"}</Badge>}>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
          <Kpi testid="kpi-anomalies"  label="Total signals" value={fmt(ovr.anomalies_total)} icon={AlertTriangle} tone={ovr.anomalies_total > 0 ? "amber" : "emerald"} />
          <Kpi testid="kpi-checks"     label="Checks run" value={fmt(ovr.checks_run)} icon={CheckCircle2} tone="slate" />
          <Kpi testid="kpi-engine"     label="Engine" value={<span className="text-[11px] font-mono">{ovr.engine?.split('/')[1] || ovr.engine}</span>} icon={Activity} tone="violet" />
          <Kpi testid="kpi-rules"      label="Rules monitored" value={fmt((ovr.rules || []).length)} icon={FileCheck} tone="slate" />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
          {(ovr.rules || []).map(r => (
            <div key={r.rule} data-testid={`ovr-rule-${r.rule}`} className="rounded-lg border bg-slate-50/40 px-3 py-2 flex items-center justify-between">
              <div>
                <div className="text-[11.5px] font-mono text-slate-800">{r.rule}</div>
                <div className="text-[10px] text-slate-500">severity: {r.severity}</div>
              </div>
              <Badge variant="outline" className={`text-[10.5px] ${r.count > 0 ? "bg-rose-50 text-rose-700 border-rose-200" : "bg-emerald-50 text-emerald-700 border-emerald-200"}`}>
                {fmt(r.count)}
              </Badge>
            </div>
          ))}
        </div>
      </Card>

      {/* AML + Sanctions side-by-side */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card title="AML pattern engine · 4 rules" icon={Eye} testid="card-aml">
          <div className="text-[11px] text-slate-500 mb-2 font-mono">{aml.engine}</div>
          <div className="flex flex-wrap gap-1.5 mb-3">
            {(aml.rules || []).map(r => <Badge key={r} variant="outline" className="text-[10px] font-mono">{r}</Badge>)}
          </div>
          <div className="grid grid-cols-4 gap-2">
            {["critical", "high", "medium", "low"].map(sev => (
              <div key={sev} data-testid={`aml-${sev}`} className={`rounded-lg border px-2.5 py-2 text-center ${
                sev === "critical" ? "bg-rose-50 border-rose-200" :
                sev === "high" ? "bg-orange-50 border-orange-200" :
                sev === "medium" ? "bg-amber-50 border-amber-200" : "bg-slate-50 border-slate-200"
              }`}>
                <div className="text-[10px] uppercase tracking-wider text-slate-500">{sev}</div>
                <div className="text-xl font-semibold tabular-nums mt-1">{fmt(aml.open_by_severity?.[sev] || 0)}</div>
              </div>
            ))}
          </div>
          <div className="text-[11px] text-slate-500 mt-3" data-testid="aml-24h">{fmt(aml.fired_24h)} alerts fired in the last 24h</div>
        </Card>

        <Card title="Sanctions screening · OFAC SDN" icon={ShieldCheck} testid="card-sanctions">
          <div className="flex items-center gap-2 mb-3">
            <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200 text-[10.5px]">{san.mode?.toUpperCase()}</Badge>
            <span className="text-[11px] text-slate-500">provider: {san.provider}</span>
          </div>
          <div className="grid grid-cols-3 gap-2">
            <Kpi testid="san-clear"   label="Clear" value={fmt(san.clear)} tone="emerald" />
            <Kpi testid="san-review"  label="Review queue" value={fmt(san.review_queue)} tone={san.review_queue > 0 ? "amber" : "slate"} />
            <Kpi testid="san-blocked" label="Blocked" value={fmt(san.blocked)} tone={san.blocked > 0 ? "rose" : "slate"} />
          </div>
          <div className="text-[11px] text-slate-500 mt-3">{fmt(san.total)} total screens performed.</div>
        </Card>
      </div>

      {/* Lender auto-alerts + stuck-ops */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card title="Lender credit-risk auto-alerts · last 30d" icon={Activity} testid="card-lender-alerts">
          <div className="text-[11px] text-slate-500 mb-3 font-mono">{lend.engine} · window {lend.window_days}d</div>
          <div className="grid grid-cols-2 gap-2">
            <Kpi testid="lend-cap" label="Cap saturation" value={fmt(lend.by_kind?.cap_saturation || 0)}
                 sub="buyer used ≥ 80% of Allianz cover" tone={lend.by_kind?.cap_saturation > 0 ? "amber" : "emerald"} />
            <Kpi testid="lend-drop" label="Sales drop" value={fmt(lend.by_kind?.sales_drop || 0)}
                 sub="≥ 30% drop vs 30-60d prior" tone={lend.by_kind?.sales_drop > 0 ? "amber" : "emerald"} />
          </div>
          <div className="text-[11px] text-slate-500 mt-3">Total {fmt(lend.total)} alerts fired in window. Lenders receive Resend email + audit row per fire.</div>
        </Card>

        <Card title={`Stuck-ops watchtower · ${fmt(stuck.total)} items`} icon={Clock} testid="card-stuck-ops">
          <div className="space-y-1.5">
            {Object.entries(stuck.categories || {}).map(([cat, n]) => (
              <div key={cat} data-testid={`stuck-${cat}`} className="flex items-center justify-between text-[12px] border-b border-slate-100 last:border-0 py-1.5">
                <span className="font-mono text-slate-700">{cat}</span>
                <Badge variant="outline" className={`tabular-nums ${n > 0 ? "bg-amber-50 text-amber-700 border-amber-200" : "bg-emerald-50 text-emerald-700 border-emerald-200"} text-[10.5px]`}>{fmt(n)}</Badge>
              </div>
            ))}
          </div>
        </Card>
      </div>
    </div>
  );
}

// ═════════════════════════════════════════════════════════════════════════
// TAB 3 · ACCESS GOVERNANCE
// ═════════════════════════════════════════════════════════════════════════
function AccessGovernanceTab() {
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get("/partner/banking/security/access-governance").then(r => setData(r.data)).catch(() => {});
  }, []);
  if (!data) return <div className="text-sm text-slate-500" data-testid="access-loading">Loading access governance…</div>;

  const r = data.admin_roster || {};
  return (
    <div className="space-y-5" data-testid="access-governance-tab">
      {/* Admin roster */}
      <Card title="Admin roster — scope-role + MFA posture" icon={Users} testid="card-admin-roster">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
          <Kpi testid="ar-total"    label="Total admins" value={fmt(r.total)} icon={Users} tone="slate" />
          <Kpi testid="ar-totp"     label="TOTP enrolled" value={fmt(r.totp_enabled)} icon={KeyRound} tone={r.totp_enabled > 0 ? "emerald" : "rose"} />
          <Kpi testid="ar-sms"      label="SMS 2FA"  value={fmt(r.sms_enabled)} icon={KeyRound} tone={r.sms_enabled > 0 ? "emerald" : "amber"} />
          <Kpi testid="ar-dual"     label="Dual MFA"
               value={<span>{fmt(r.dual_mfa)} <span className="text-[12px] text-slate-400">({r.dual_mfa_pct}%)</span></span>}
               icon={ShieldCheck} tone={r.dual_mfa_pct >= 80 ? "emerald" : r.dual_mfa_pct > 0 ? "amber" : "rose"} />
        </div>
        <div className="rounded-lg border bg-slate-50/60 px-3 py-2.5">
          <div className="text-[11px] uppercase tracking-wider text-slate-500 mb-2">By scope role</div>
          <div className="flex flex-wrap gap-2" data-testid="ar-roles">
            {Object.entries(r.by_scope_role || {}).map(([role, count]) => (
              <div key={role} className="inline-flex items-center gap-1.5 rounded-lg bg-white border px-2.5 py-1">
                <span className="text-[11px] font-mono text-slate-700">{role}</span>
                <Badge variant="outline" className="text-[10px] tabular-nums">{count}</Badge>
              </div>
            ))}
          </div>
        </div>
      </Card>

      {/* Active elevations */}
      <Card title={`Active temporary elevations (${data.elevations_active_count})`} icon={KeyRound} testid="card-elevations">
        {(data.elevations_active || []).length === 0 ? (
          <div className="text-sm text-slate-500 italic" data-testid="elev-empty">No active elevations. (Baseline: every admin runs at their permanent scope-role.)</div>
        ) : (
          <div className="space-y-2 max-h-72 overflow-y-auto">
            {data.elevations_active.map((e, i) => (
              <div key={e.id || i} data-testid={`elev-${i}`} className="rounded-lg border bg-amber-50/40 border-amber-200 px-3 py-2">
                <div className="flex items-center justify-between gap-2">
                  <div className="font-mono text-[11.5px]">
                    <span className="text-slate-800">{e.target_actor}</span>
                    <span className="text-slate-400 mx-1">←</span>
                    <span className="text-slate-600">approver {e.approver_actor}</span>
                  </div>
                  <Badge variant="outline" className="text-[10px]">{e.from_role} → {e.to_role}</Badge>
                </div>
                <div className="text-[10.5px] text-slate-600 mt-1.5">expires {e.expires_at ? new Date(e.expires_at).toLocaleString() : "—"}</div>
                <div className="text-[10.5px] italic text-slate-500 mt-0.5">"{e.reason}"</div>
              </div>
            ))}
          </div>
        )}
      </Card>

      {/* Break-glass + Read-audit + Exports */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card title={`Break-glass · 30d (${data.break_glass_open} open)`} icon={ShieldAlert} testid="card-bg">
          {(data.break_glass_30d || []).length === 0 ? (
            <div className="text-sm text-slate-500 italic" data-testid="bg-empty">No break-glass requests in 30d.</div>
          ) : (
            <div className="space-y-2 max-h-60 overflow-y-auto">
              {data.break_glass_30d.slice(0, 20).map((r, i) => (
                <div key={r.id || i} data-testid={`bg-${i}`} className="rounded-lg border px-2.5 py-1.5 text-[11px]">
                  <div className="flex items-center justify-between gap-2">
                    <span className="font-mono">{r.requester_actor}</span>
                    <Badge variant="outline" className={`text-[9.5px] ${r.status === "active" || r.status === "pending" ? "bg-amber-50 text-amber-700 border-amber-200" : "bg-slate-50 text-slate-500"}`}>{r.status}</Badge>
                  </div>
                  <div className="text-[10px] font-mono text-slate-500 mt-0.5">{r.scope}</div>
                </div>
              ))}
            </div>
          )}
        </Card>

        <Card title="PII reveal audit · last 7d" icon={Eye} testid="card-reads">
          <Kpi testid="reads-total" label="Total reveals" value={fmt(data.pii_reveals_7d?.total)} tone={data.pii_reveals_7d?.total > 100 ? "amber" : "slate"} />
          <div className="text-[10.5px] uppercase tracking-wider text-slate-500 mt-3 mb-1.5">Top actors</div>
          {(data.pii_reveals_7d?.top_actors || []).length === 0 ? (
            <div className="text-[11px] text-slate-500 italic">No PII reveals in window.</div>
          ) : (
            <div className="space-y-1">
              {data.pii_reveals_7d.top_actors.slice(0, 5).map((a, i) => (
                <div key={i} data-testid={`reads-actor-${i}`} className="flex items-center justify-between text-[11px]">
                  <span className="font-mono">{a.actor}</span>
                  <Badge variant="outline" className="tabular-nums text-[10px]">{fmt(a.count)}</Badge>
                </div>
              ))}
            </div>
          )}
        </Card>

        <Card title="Data exports" icon={FileSearch} testid="card-exports">
          <Kpi testid="exp-24h" label="Exports · 24h" value={fmt(data.data_exports?.exports_24h)} tone={data.data_exports?.exports_24h > 5 ? "amber" : "slate"} />
          <div className="mt-3">
            <Kpi testid="exp-30d" label="Exports · 30d" value={fmt(data.data_exports?.exports_30d)} tone="slate" />
          </div>
          <div className="text-[10.5px] text-slate-500 mt-3">Per-role hourly caps enforced in middleware. Engineering 25K/h, finance 5K/h, support_l2 2K/h, support_l1 500/h.</div>
        </Card>
      </div>
    </div>
  );
}

// ═════════════════════════════════════════════════════════════════════════
// TAB 4 · CYBER POSTURE
// ═════════════════════════════════════════════════════════════════════════
function CyberPostureTab() {
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get("/partner/banking/security/cyber-posture").then(r => setData(r.data)).catch(() => {});
  }, []);
  if (!data) return <div className="text-sm text-slate-500" data-testid="cyber-loading">Loading cyber posture…</div>;

  const chain = data.audit_chain_integrity || {};
  const docs = data.compliance_documents || {};
  const bcp = data.bcp_dr || {};
  const inc = data.incident_register || {};

  return (
    <div className="space-y-5" data-testid="cyber-posture-tab">
      {/* Audit chain integrity */}
      <Card title="Tamper-evidence · hash-chained audit trail" icon={Lock} testid="card-chain"
            action={chain.valid
              ? <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200">✓ VERIFIED</Badge>
              : <Badge variant="outline" className="bg-rose-50 text-rose-700 border-rose-200">✗ BROKEN</Badge>}>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Kpi testid="chain-rows" label="Verified rows" value={fmt(chain.verified_rows)} tone="emerald" icon={CheckCircle2} />
          <Kpi testid="chain-head" label="Head position" value={chain.head_position ?? "—"} tone="slate" icon={Activity} />
          <Kpi testid="chain-hash" label="Head hash" value={<span className="text-[11px] font-mono break-all">{chain.head_hash || "—"}</span>} tone="slate" />
          <Kpi testid="chain-broken" label="Broken at"  value={chain.broken_at ?? "—"} tone={chain.broken_at ? "rose" : "emerald"} />
        </div>
        <div className="text-[11px] text-slate-500 mt-3">
          Every admin action is hash-linked to the prior. Any out-of-band mutation (DB tampering, manual edit) flips this card from VERIFIED to BROKEN within minutes.
          Banks can independently re-verify via the public-key audit-feed published to your sponsor-bank SFTP drop.
        </div>
      </Card>

      {/* Compliance documents */}
      <Card title={`Third-party attestations · ${fmt(docs.real_uploaded)} / ${fmt(docs.total)} live`} icon={Award} testid="card-attestations">
        <div className="space-y-2">
          {(docs.docs || []).map(d => (
            <div key={d.doc_id} data-testid={`doc-${d.doc_id}`} className="flex items-center justify-between gap-3 rounded-lg border bg-slate-50/40 px-3 py-2">
              <div className="flex-1">
                <div className="text-[12.5px] font-medium text-slate-800">{d.title}</div>
                <div className="text-[10.5px] text-slate-500">target: {d.target_q || "—"}{d.current_version ? ` · v${d.current_version}` : ""}{d.uploaded_at ? ` · uploaded ${new Date(d.uploaded_at).toLocaleDateString()}` : ""}</div>
              </div>
              {d.is_placeholder
                ? <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200 text-[10px]">PLACEHOLDER</Badge>
                : <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200 text-[10px]">AVAILABLE</Badge>}
              {d.nda_required && <Badge variant="outline" className="text-[9.5px] bg-slate-100">NDA</Badge>}
            </div>
          ))}
        </div>
      </Card>

      {/* BCP/DR + Incident register */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card title="Business continuity / DR" icon={ShieldCheck} testid="card-bcp">
          <div className="flex items-center gap-2 mb-3">
            {bcp.doc_present
              ? <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200">DOC ON FILE</Badge>
              : <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200">PLACEHOLDER</Badge>}
            {bcp.doc_version && <span className="text-[11px] text-slate-500">v{bcp.doc_version}</span>}
          </div>
          <div className="text-[11.5px] text-slate-600 leading-relaxed">
            <strong>RPO</strong>: 15 min (MongoDB Atlas continuous backup).<br/>
            <strong>RTO</strong>: 4 hours (multi-AZ failover + Kubernetes self-heal).<br/>
            <strong>Tabletop cadence</strong>: quarterly. Last exercise: see attached BCP/DR PDF.
          </div>
          {bcp.target_quarter && <div className="text-[10.5px] text-slate-400 mt-2">target quarter: {bcp.target_quarter}</div>}
        </Card>

        <Card title="Incident register" icon={ServerCrash} testid="card-incident">
          <Kpi testid="inc-count" label="Cyber incidents · last 12mo" value={fmt(inc.last_12mo_count)}
               tone={inc.last_12mo_count > 0 ? "rose" : "emerald"} />
          <div className="text-[11px] text-slate-600 mt-3 italic">{inc.note}</div>
          <div className="text-[10.5px] text-slate-400 mt-2">Register is append-only · disclosed to sponsor banks within 24h of detection per FFIEC guidance.</div>
        </Card>
      </div>
    </div>
  );
}

// ═════════════════════════════════════════════════════════════════════════
// PAGE SHELL
// ═════════════════════════════════════════════════════════════════════════
export function BankingSecurityPosture() {
  const [summary, setSummary] = useState(null);
  useEffect(() => {
    api.get("/partner/banking/security/posture-summary").then(r => setSummary(r.data)).catch(() => {});
  }, []);

  const ksTone = summary?.kill_switch_level ? KS_TONE[summary.kill_switch_level] : KS_TONE.normal;

  return (
    <div className="space-y-5" data-testid="banking-security-posture">
      <div>
        <div className="flex items-center gap-3 flex-wrap">
          <h1 className="text-2xl font-semibold text-slate-900 flex items-center gap-2">
            <Shield className="w-5 h-5 text-slate-500" /> Security &amp; Fraud Posture
          </h1>
          <LiveRibbon />
        </div>
        <p className="text-[13px] text-slate-500 mt-1">
          Executive scorecard for sponsor-bank risk officers — every defense layer Verqo runs, fully live, PII-redacted at the edge.
        </p>
      </div>

      {/* Top KPI strip */}
      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3" data-testid="posture-summary-strip">
          <div data-testid="kpi-ks" className={`rounded-xl border px-4 py-3 ${ksTone}`}>
            <div className="text-[10.5px] uppercase tracking-wider opacity-80">Kill switch</div>
            <div className="text-base font-semibold mt-1.5 capitalize">{summary.kill_switch_level?.replace("_", " ")}</div>
            {summary.kill_switch_reason && <div className="text-[10.5px] opacity-80 mt-0.5 line-clamp-1" title={summary.kill_switch_reason}>{summary.kill_switch_reason}</div>}
          </div>
          <Kpi testid="kpi-summ-failed-24h" label="Failed logins 24h" value={fmt(summary.failed_logins_24h)} icon={ShieldAlert} tone={summary.failed_logins_24h > 200 ? "rose" : "emerald"} />
          <Kpi testid="kpi-summ-rl-24h" label="Rate-limit hits 24h" value={fmt(summary.rate_limit_hits_24h)} icon={Gauge} tone={summary.rate_limit_hits_24h > 100 ? "amber" : "emerald"} />
          <Kpi testid="kpi-summ-aml" label="AML alerts open" value={fmt(summary.aml_alerts_open)} icon={Eye} tone={summary.aml_alerts_open > 0 ? "amber" : "emerald"} />
          <Kpi testid="kpi-summ-sanc" label="Sanctions review" value={fmt(summary.sanctions_review_queue)} icon={ShieldCheck} tone={summary.sanctions_review_queue > 0 ? "amber" : "emerald"} />
          <Kpi testid="kpi-summ-ovr" label="Admin overrides 24h" value={fmt(summary.overrides_24h)} icon={Activity} tone="slate" />
          <Kpi testid="kpi-summ-bg" label="Break-glass open" value={fmt(summary.break_glass_open)} icon={ShieldAlert} tone={summary.break_glass_open > 0 ? "amber" : "emerald"} />
        </div>
      )}

      <Tabs defaultValue="threat" className="w-full">
        <TabsList data-testid="posture-tabs">
          <TabsTrigger value="threat"   data-testid="tab-threat">Threat Defense</TabsTrigger>
          <TabsTrigger value="fraud"    data-testid="tab-fraud">Fraud Detection</TabsTrigger>
          <TabsTrigger value="access"   data-testid="tab-access">Access Governance</TabsTrigger>
          <TabsTrigger value="cyber"    data-testid="tab-cyber">Cyber Posture</TabsTrigger>
        </TabsList>
        <TabsContent value="threat" className="mt-4"><ThreatDefenseTab /></TabsContent>
        <TabsContent value="fraud"  className="mt-4"><FraudDetectionTab /></TabsContent>
        <TabsContent value="access" className="mt-4"><AccessGovernanceTab /></TabsContent>
        <TabsContent value="cyber"  className="mt-4"><CyberPostureTab /></TabsContent>
      </Tabs>
    </div>
  );
}
